// File: frontend/src/pages/Opportunities/OpportunitiesPage.jsx
// Opportunities management page with sales pipeline management

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { ExportOpportunitiesButton } from '../../components/Export/ExportButton';
import OpportunityForm from '../../components/Opportunities/OpportunityForm';
import CloseOpportunityModal from '../../components/Opportunities/CloseOpportunityModal';
import {
  CurrencyDollarIcon,
  TrophyIcon,
  ChartBarIcon,
  ClockIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ArrowRightIcon,
  BuildingOfficeIcon,
  UserIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const OpportunitiesPage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('active'); // active, won, lost, all
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [sortBy, setSortBy] = useState('value');
  const [sortOrder, setSortOrder] = useState('desc');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  const [closeModal, setCloseModal] = useState({ opportunity: null, outcome: null });

  // Fetch opportunities data
  const { data: opportunitiesData, isLoading, error } = useQuery(
    ['opportunities', { 
      search: searchTerm, 
      page: currentPage, 
      pageSize, 
      stage: selectedStage, 
      user: selectedUser,
      status: selectedStatus,
      minValue,
      maxValue,
      sortBy,
      sortOrder
    }],
    async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        sort: `${sortBy}:${sortOrder}`
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (selectedStage) params.append('stage', selectedStage);
      if (selectedUser) params.append('user_id', selectedUser);
      if (minValue) params.append('min_value', minValue);
      if (maxValue) params.append('max_value', maxValue);
      
      // Handle status filter
      if (selectedStatus === 'active') {
        // For active opportunities, exclude closed stages
        params.append('is_won', 'false');
        params.append('is_lost', 'false');
      } else if (selectedStatus === 'won') {
        params.append('stage', 'closed_won');
      } else if (selectedStatus === 'lost') {
        params.append('stage', 'closed_lost');
      }
      
      const response = await api.get(`/opportunities?${params}`);
      return response.data;
    },
    {
      enabled: !!user?.id,
      keepPreviousData: true,
    }
  );

  // Fetch dashboard stats
  const { data: statsData } = useQuery(
    'opportunities-stats',
    async () => {
      const response = await api.get('/opportunities/stats/dashboard');
      return response.data.data;
    },
    { enabled: !!user?.id }
  );

  // Fetch users for filter
  const { data: users } = useQuery(
    'users-list',
    async () => {
      const response = await api.get('/users?limit=100');
      return response.data.data || response.data.users || [];
    },
    { enabled: !!user?.id }
  );

  // Delete opportunity mutation
  const deleteMutation = useMutation(
    (opportunityId) => api.delete(`/opportunities/${opportunityId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('opportunities');
        queryClient.invalidateQueries('opportunities-stats');
        toast.success('Oportunidade excluída com sucesso!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao excluir oportunidade');
      },
    }
  );

  // Update stage mutation
  const updateStageMutation = useMutation(
    ({ id, stage }) => api.put(`/opportunities/${id}`, { stage }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('opportunities');
        queryClient.invalidateQueries('opportunities-stats');
        toast.success('Etapa atualizada com sucesso!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar stage');
      },
    }
  );

  const opportunities = opportunitiesData?.data || [];
  const totalOpportunities = opportunitiesData?.pagination?.total_items || 0;
  const totalPages = Math.ceil(totalOpportunities / pageSize);

  // Stage definitions
  const stages = [
    { key: 'lead', label: 'Lead', color: 'bg-gray-100 text-gray-800' },
    { key: 'qualified', label: 'Qualificado', color: 'bg-blue-100 text-blue-800' },
    { key: 'proposal', label: 'Proposta', color: 'bg-yellow-100 text-yellow-800' },
    { key: 'negotiation', label: 'Negociação', color: 'bg-orange-100 text-orange-800' },
    { key: 'closed_won', label: 'Fechou Ganho', color: 'bg-green-100 text-green-800' },
    { key: 'closed_lost', label: 'Fechou Perdido', color: 'bg-red-100 text-red-800' }
  ];

  const getStageColor = (stage) => {
    const stageObj = stages.find(s => s.key === stage);
    return stageObj?.color || 'bg-gray-100 text-gray-800';
  };

  const getStageLabel = (stage) => {
    const stageObj = stages.find(s => s.key === stage);
    return stageObj?.label || stage;
  };

  const handleDeleteOpportunity = (opportunity) => {
    if (window.confirm(`Tem certeza que deseja excluir a oportunidade "${opportunity.title}"?`)) {
      deleteMutation.mutate(opportunity.id);
    }
  };

  const handleAdvanceStage = (opportunity) => {
    const currentStageIndex = stages.findIndex(s => s.key === opportunity.stage);
    if (currentStageIndex < stages.length - 3) { // Don't advance from negotiation to closed automatically
      const nextStage = stages[currentStageIndex + 1];
      updateStageMutation.mutate({ id: opportunity.id, stage: nextStage.key });
    }
  };

  const handleEditOpportunity = (opportunity) => {
    setEditingOpportunity(opportunity);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingOpportunity(null);
    queryClient.invalidateQueries('opportunities');
    queryClient.invalidateQueries('opportunities-stats');
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingOpportunity(null);
  };

  const canAdvanceStage = (stage) => {
    return !['negotiation', 'closed_won', 'closed_lost'].includes(stage);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Não definida';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatPercentage = (value) => {
    return `${(value || 0)}%`;
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Gestão de Oportunidades
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Pipeline de vendas e oportunidades comerciais
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <ExportOpportunitiesButton
              variant="secondary"
              size="md"
              filters={{
                stage: selectedStage || undefined,
                user_id: selectedUser || undefined
              }}
            />
            <button
              onClick={() => setShowForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Nova Oportunidade</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total do Pipeline</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency(statsData?.basic?.pipeline_value || 0)}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Oportunidades Ativas</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {statsData?.basic?.total_opportunities - statsData?.basic?.closed_won - statsData?.basic?.closed_lost || 0}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <TrophyIcon className="h-8 w-8 text-yellow-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Taxa de Conversão</p>
              {(statsData?.total_closed ?? 0) === 0 ? (
                <p className="text-sm font-semibold text-gray-400 dark:text-gray-500 mt-1">
                  Sem oportunidades fechadas
                </p>
              ) : (
                <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {formatPercentage(statsData?.win_rate || 0)}
                </p>
              )}
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Valor Médio</p>
              <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency((statsData?.basic?.pipeline_value || 0) / (statsData?.basic?.total_opportunities || 1))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
          {/* Search */}
          <div className="flex-1 max-w-lg">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar oportunidades por título ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Filter toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-950"
          >
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span>Filtros</span>
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stage
                </label>
                <select
                  value={selectedStage}
                  onChange={(e) => setSelectedStage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos os stages</option>
                  {stages.map(stage => (
                    <option key={stage.key} value={stage.key}>{stage.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">Todas</option>
                  <option value="active">Ativas</option>
                  <option value="won">Ganhas</option>
                  <option value="lost">Perdidas</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Mínimo
                </label>
                <input
                  type="number"
                  placeholder="R$ 0"
                  value={minValue}
                  onChange={(e) => setMinValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor Máximo
                </label>
                <input
                  type="number"
                  placeholder="R$ 999.999"
                  value={maxValue}
                  onChange={(e) => setMaxValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsável
                </label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos os usuários</option>
                  {users?.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Opportunities Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Oportunidades ({totalOpportunities})
          </h3>
        </div>
        
        {isLoading ? (
          <div className="p-6">
            <div className="animate-pulse">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="grid grid-cols-8 gap-4 py-4">
                    <div className="h-4 bg-gray-200 rounded col-span-2"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <div className="text-red-600 mb-4">
              <ExclamationTriangleIcon className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Erro ao carregar oportunidades
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              {error.response?.data?.message || 'Tente novamente mais tarde'}
            </p>
          </div>
        ) : opportunities.length === 0 ? (
          <div className="p-6 text-center">
            <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Nenhuma oportunidade encontrada
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              Tente ajustar os filtros ou criar uma nova oportunidade.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 dark:bg-gray-950">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('title')}
                  >
                    Título {getSortIcon('title')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Contato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Stage
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('value')}
                  >
                    Valor {getSortIcon('value')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Probabilidade
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('expected_close_date')}
                  >
                    Data Esperada {getSortIcon('expected_close_date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Responsável
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                {opportunities.map((opportunity) => (
                  <tr key={opportunity.id} className="hover:bg-gray-50 dark:bg-gray-950">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <Link to={`/opportunities/${opportunity.id}`}>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600">
                            {opportunity.title}
                          </div>
                        </Link>
                        {opportunity.description && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                            {opportunity.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {opportunity.company?.name || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {opportunity.contact ? 
                            `${opportunity.contact.first_name} ${opportunity.contact.last_name}` : 
                            'N/A'
                          }
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStageColor(opportunity.stage)}`}>
                        {getStageLabel(opportunity.stage)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(opportunity.value)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {formatPercentage(opportunity.probability)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(opportunity.expected_close_date)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {opportunity.user ? 
                        `${opportunity.user.first_name} ${opportunity.user.last_name}` : 
                        'N/A'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link to={`/opportunities/${opportunity.id}`}>
                          <button
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                            title="Ver detalhes"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </Link>
                        <button
                          onClick={() => handleEditOpportunity(opportunity)}
                          className="p-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 rounded"
                          title="Editar oportunidade"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        {canAdvanceStage(opportunity.stage) && (
                          <button
                            onClick={() => handleAdvanceStage(opportunity)}
                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                            title="Avançar stage"
                            disabled={updateStageMutation.isLoading}
                          >
                            <ArrowRightIcon className="h-4 w-4" />
                          </button>
                        )}
                        {!['closed_won', 'closed_lost'].includes(opportunity.stage) && (
                          <>
                            <button
                              onClick={() => setCloseModal({ opportunity, outcome: 'won' })}
                              className="p-1 text-yellow-600 hover:bg-yellow-100 rounded"
                              title="Marcar como ganha"
                            >
                              <TrophyIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setCloseModal({ opportunity, outcome: 'lost' })}
                              className="p-1 text-red-600 hover:bg-red-100 rounded"
                              title="Marcar como perdida"
                            >
                              <XCircleIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDeleteOpportunity(opportunity)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          title="Excluir oportunidade"
                          disabled={deleteMutation.isLoading}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Mostrando {((currentPage - 1) * pageSize) + 1} a {Math.min(currentPage * pageSize, totalOpportunities)} de {totalOpportunities} oportunidades
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-950"
                >
                  Anterior
                </button>
                <span className="px-3 py-1 text-sm">
                  {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:bg-gray-950"
                >
                  Próximo
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Opportunity Form Modal */}
      <OpportunityForm
        isOpen={showForm}
        onClose={handleCloseForm}
        opportunity={editingOpportunity}
        onSuccess={handleFormSuccess}
      />

      <CloseOpportunityModal
        opportunity={closeModal.opportunity}
        outcome={closeModal.outcome}
        isOpen={!!closeModal.opportunity}
        onClose={() => setCloseModal({ opportunity: null, outcome: null })}
        onSuccess={() => {
          setCloseModal({ opportunity: null, outcome: null });
          queryClient.invalidateQueries('opportunities');
          queryClient.invalidateQueries('opportunities-stats');
        }}
      />
    </div>
  );
};

export default OpportunitiesPage;